import { useState, useEffect } from 'react';
import { X, Loader2, Cog } from 'lucide-react';
import { useGetAllMachines } from '../hooks/useProductionPlanning';

// ROT-209: lets a planner assign which machine runs EACH manufacturing-route
// step of an order, from the Planning Board — previously only the order's
// single top-level machine could be set there, so an order whose processes
// run on different machines (one after another, or in parallel) only ever
// showed up on whichever machine happened to be set at the order level.
// Reuses the same per-route machineName field/endpoint already used by the
// route editor in ProductionOrders.tsx — no new schema.

interface RouteRow {
  id: string;
  sequence: number;
  process: string;
  machineName: string | null;
  status: string;
}

interface ProcessMachineAssignModalProps {
  orderId: string;
  orderNumber: string;
  onClose: () => void;
  onAssigned?: () => void;
}

const statusLabel = (status: string) =>
  status === 'in_progress' ? 'En proceso' : status === 'completed' ? 'Completado' : 'Pendiente';

const statusColor = (status: string) =>
  status === 'in_progress' ? 'bg-blue-100 text-blue-700' : status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500';

export default function ProcessMachineAssignModal({ orderId, orderNumber, onClose, onAssigned }: ProcessMachineAssignModalProps) {
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const { data: machines } = useGetAllMachines();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/production-orders/${orderId}/manufacturing-routes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setRoutes([...data].sort((a: RouteRow, b: RouteRow) => a.sequence - b.sequence));
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orderId]);

  const assignMachine = async (routeId: string, machineName: string) => {
    setSavingId(routeId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/production-orders/${orderId}/manufacturing-routes/${routeId}/machine`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ machineName: machineName || null }),
      });
      if (res.ok) {
        const updated = await res.json();
        setRoutes(prev => prev.map(r => (r.id === routeId ? { ...r, machineName: updated.machineName } : r)));
        onAssigned?.();
      }
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90]" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b bg-blue-700 text-white rounded-t-lg">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2">
              <Cog className="h-4 w-4" /> Máquinas por Proceso
            </h3>
            <p className="text-xs opacity-80">{orderNumber}</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : routes.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">Esta orden no tiene rutas de fabricación definidas.</p>
          ) : (
            <div className="space-y-3">
              {routes.map(route => (
                <div key={route.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-800">#{route.sequence} {route.process}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColor(route.status)}`}>
                      {statusLabel(route.status)}
                    </span>
                  </div>
                  <select
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm disabled:opacity-50"
                    value={route.machineName || ''}
                    onChange={e => assignMachine(route.id, e.target.value)}
                    disabled={savingId === route.id}
                  >
                    <option value="">-- Sin asignar --</option>
                    {machines?.map(m => (
                      <option key={m.id} value={m.name}>{m.name}</option>
                    ))}
                  </select>
                  {savingId === route.id && <p className="text-[10px] text-gray-400 mt-1">Guardando...</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
